import { useMemo } from "react";
import Highcharts from "highcharts";
import { useTheme } from "@mui/material";
import { useChartColors } from "@hooks";

export const useChartOptions = (
  data: number[],
  color?: string,
  height?: number
): Highcharts.Options => {
  const theme = useTheme();
  const chartColors = useChartColors("miniChart");

  const lineColor = color ?? chartColors.line;
  const areaColor = color ?? chartColors.area;

  return useMemo(() => {
    const hasData = data.length > 0;
    const dataMin = hasData ? Math.min(...data) : 0;
    const dataMax = hasData ? Math.max(...data) : 0;
    const range = dataMax - dataMin;
    const yMin = range > 0 ? dataMin - range * 0.1 : dataMin - 1;
    const yMax = range > 0 ? dataMax + range * 0.1 : dataMax + 1;

    return {
      chart: {
        type: "areaspline",
        height,
        backgroundColor: "transparent",
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
      },
      title: undefined,
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: { enabled: false },
      xAxis: { visible: false },
      yAxis: {
        visible: false,
        ...(hasData && { min: yMin, max: yMax }),
      },
      plotOptions: {
        series: {
          animation: { duration: 400 },
          states: { hover: { enabled: false } },
          marker: { enabled: false },
          enableMouseTracking: false,
        },
        areaspline: {
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [
                0.2,
                Highcharts.color(areaColor)
                  .setOpacity(0.2)
                  .get("rgba") as string,
              ],
              [
                1,
                Highcharts.color(theme.palette.common.white)
                  .setOpacity(0)
                  .get("rgba") as string,
              ],
            ],
          },
          lineColor,
          lineWidth: 2,
          threshold: null,
        },
      },
      series: [
        {
          type: "areaspline",
          data,
        },
      ],
    };
  }, [data, lineColor, areaColor, height, theme]);
};
